import { workouts, type WorkoutDetail } from '../../services/api';

/** Unwrap GET /workout/{id}/details — zones, peaks, lap_data live here. */
export function parseExtendedWorkoutDetails(apiBody: unknown): Record<string, unknown> | null {
  if (!apiBody || typeof apiBody !== 'object') return null;
  const root = apiBody as Record<string, unknown>;
  const envelope = root.data;
  if (envelope && typeof envelope === 'object') {
    const env = envelope as Record<string, unknown>;
    const nested = env.workoutDetails ?? env.data ?? env;
    if (nested && typeof nested === 'object') return nested as Record<string, unknown>;
  }
  const direct = root.workoutDetails ?? root;
  return direct && typeof direct === 'object' ? (direct as Record<string, unknown>) : null;
}

/** Merge lap_data + aggregates from /details onto the base workout record. */
export function mergeWorkoutWithExtendedDetails(
  workout: WorkoutDetail | null | undefined,
  extended: Record<string, unknown> | null,
): WorkoutDetail | null {
  if (!workout) return null;
  if (!extended) return workout;

  const lapData = extended.lap_data;
  const laps = Array.isArray(lapData) ? lapData : workout.details?.lap_data ?? [];

  const baseDetails = workout.details ?? {
    zones: { hr_zones: [], pw_zones: [] },
    peaks: {},
    lap_data: [],
    min_max_avg: {},
  };

  return {
    ...workout,
    details: {
      ...baseDetails,
      zones: (extended.zones as typeof baseDetails.zones) ?? baseDetails.zones,
      peaks: (extended.peaks as typeof baseDetails.peaks) ?? baseDetails.peaks,
      lap_data: laps,
      fit_metrics: (extended.fit_metrics as import('../../utils/fitRecordedMetrics').FitRecordedMetadata | null) ?? workout.details?.fit_metrics,
      min_max_avg: (extended.min_max_avg as typeof baseDetails.min_max_avg) ?? baseDetails.min_max_avg,
      data: baseDetails.data,
    },
  };
}

/** Meta + extended details in parallel (Ai-Tempo getWorkout parity). */
export async function loadWorkoutWithDetails(workoutId: number): Promise<WorkoutDetail> {
  const [metaSettled, detailsSettled] = await Promise.allSettled([
    workouts.getWorkoutDetail(workoutId),
    workouts.getWorkoutDetails(workoutId),
  ]);

  if (metaSettled.status === 'rejected') throw metaSettled.reason;

  const res = metaSettled.value;
  const workout =
    res.data.data?.workout ??
    (res.data as unknown as { data?: WorkoutDetail }).data ??
    (res.data as unknown as WorkoutDetail);

  const extended =
    detailsSettled.status === 'fulfilled'
      ? parseExtendedWorkoutDetails(detailsSettled.value.data)
      : null;

  return mergeWorkoutWithExtendedDetails(workout, extended) ?? workout;
}
